import { css } from '@emotion/react'
import { Link, Text } from '@livechat/design-system-react-components'
import { Fragment, type MouseEvent, type ReactNode } from 'react'

import { useOneClickPaymentContext } from '@/context/OneClickPayment'
import { getMarketplaceTermsLink } from '@/utils/products/urls'

const termsCss = css`
  flex: 1;
`

const termsLink = getMarketplaceTermsLink()

const baseCopy = (
  <Fragment key="marketplaceTerms">
    By continuing you agree to <strong>Text</strong>{' '}
    <Link href={termsLink} target="_blank" rel="noopener">
      Marketplace Terms
    </Link>
  </Fragment>
)

const getTermsCopy = (
  onAppScopesClick: (event: MouseEvent<HTMLAnchorElement>) => void,
  privacyPolicyUrl?: string,
  hasScopes?: boolean,
  termsAndConditionsUrl?: string
): ReactNode[] => {
  const privacyPolicyLink = (
    <Link
      href={privacyPolicyUrl}
      target="_blank"
      rel="noopener"
      key="privacyPolicyLink"
    >
      App&nbsp;Privacy Policy
    </Link>
  )
  const appScopesLink = (
    <Link href="#app-terms" onClick={onAppScopesClick} key="appScopesLink">
      App&nbsp;Scopes
    </Link>
  )

  const termsAndConditionsLink = (
    <Link
      href={termsAndConditionsUrl}
      target="_blank"
      rel="noopener"
      key="termsAndConditionsLink"
    >
      App&nbsp;Terms and Conditions
    </Link>
  )

  const copyArray: ReactNode[] = []

  if (privacyPolicyUrl) {
    copyArray.push(privacyPolicyLink)
  }

  if (termsAndConditionsUrl) {
    copyArray.push(termsAndConditionsLink)
  }

  if (hasScopes) {
    copyArray.push(appScopesLink)
  }

  const copy: ReactNode[] = [baseCopy]
  copyArray.forEach((value, index, arr) => {
    if (arr.length - 1 === index) {
      copy.push(' &\u00A0', value)
    } else {
      copy.push(', ', value)
    }
  })

  return copy
}

export const TermsCopy = () => {
  const { paymentIntent, closeModal } = useOneClickPaymentContext()

  if (!paymentIntent) {
    return null
  }

  const { hasScopes, privacyPolicyUrl, termsAndConditionsUrl } =
    paymentIntent.metadata

  const onAppScopesClick = (e: MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault()
    closeModal()
    setTimeout(() => {
      document
        .getElementById('app-terms')
        ?.scrollIntoView({ behavior: 'smooth' })
    }, 100)
  }

  const copy = getTermsCopy(
    onAppScopesClick,
    privacyPolicyUrl,
    hasScopes,
    termsAndConditionsUrl
  )

  return (
    <Text size="xs" data-test="ocp-footer-terms" css={termsCss}>
      {copy}
    </Text>
  )
}
